import { addDays, isWithinInterval, startOfToday } from 'date-fns';
import { parseApiDateString } from '@/utils';

export const MODALS = {
  // Message 1 is the "day(s) before the test" one, generally speaking.
  MESSAGE_1: 'message_1',
  // Day of
  MESSAGE_2: 'message_2',
  // Awaiting results
  MESSAGE_3: 'message_3',
  // Post transition
  MESSAGE_4: 'message_4',
};

export function getTransitionConfig(store, classification) {
  const pv = store.getters['productVars/get']['transitionMessageConfig'];
  if (classification && pv && pv.parsedValue) {
    const transitions = pv.parsedValue.transitions;
    const transition = transitions.find(t => t.classification_ids.includes(classification.id));
    return transition;
  }
}

export function userShouldTransition(store) {
  const config = getTransitionConfig(store, store.getters['session/classification']);
  if (config) {
    const userHasPurchased = userHasExcludedPurchaseOrder(store, config);
    if (userHasPurchased) {
      return false;
    }
    const date = userDate(store, config);
    const status = userStatus(store, config);
    if (date) {
      const dateInWindow = new Date() > date;
      return dateInWindow && !status;
    }
  }
}

export function userDate(store, transition) {
  const user = store.getters['user/get'];
  const classificationConfigs = store.getters['userProfileConfigs/get'][user.classificationId];
  const userProfileConfig = classificationConfigs.find(config => config.id === transition.date_config_id) || {};
  const userProfile = user.userProfile || {};
  const date = userProfile[userProfileConfig.userProfileColumnName];
  if (date) {
    return parseApiDateString(date).date;
  }
}

export function userStatus(store, transition) {
  const user = store.getters['user/get'];
  const classificationConfigs = store.getters['userProfileConfigs/get'][user.classificationId];
  const userProfileConfig = classificationConfigs.find(config => config.id === transition.status_config_id) || {};
  const userProfile = user.userProfile || {};
  const status = userProfile[userProfileConfig.userProfileColumnName];
  if (status === true || status === false) {
    return true;
  } else {
    return false;
  }
}

function userHasExcludedPurchaseOrder(store, transition) {
  if (!store || !transition) return false;
  const excludedIds = transition.purchase_order_exclusion_ids;
  if (excludedIds && Array.isArray(excludedIds) && excludedIds.length > 0) {
    const activePurchaseReceipts = store.getters['purchaseReceipts/all'];
    const activeExcludedReceipts = excludedIds.filter(purchaseOrderId => {
      return activePurchaseReceipts.some(pr => pr.purchaseOrderId === Number(purchaseOrderId));
    });
    return activeExcludedReceipts.length > 0;
  }
  return false;
}

function getTransitionMessageFromLocalStorage(user) {
  const userTransitionKey = generateLocalStorageKey(user);
  const storedValue = localStorage.getItem(userTransitionKey);
  if (storedValue) {
    return JSON.parse(storedValue);
  }
}

function seenTransitionMessage(messageName, user) {
  const messagesSeen = getTransitionMessageFromLocalStorage(user) || [];
  return messagesSeen.includes(messageName);
}

export function recordTransitionMessageSeen(messageName, user) {
  const messagesSeen = getTransitionMessageFromLocalStorage(user) || [];
  const localStorageKey = generateLocalStorageKey(user);
  messagesSeen.push(messageName);
  localStorage.setItem(localStorageKey, JSON.stringify(messagesSeen));
}

export function showTransitionMessage(store) {
  const config = getTransitionConfig(store, store.getters['session/classification']);
  if (config) {

    // We can consider some transitions as completed or not necessary if a user has one of the
    // specified purchase orders listed in the transition config
    const userHasPurchased = userHasExcludedPurchaseOrder(store, config);
    if (userHasPurchased) return false;

    // don't show any of the pre date messages if the user has a status or it is past the date
    // this state shouldn't really ever get triggered but just to be safe we are checking
    // ex: status is passing a test and the user has already passed it
    const hasStatus = userStatus(store, config);
    let today = new Date();
    const savedDate = userDate(store, config);
    if (hasStatus || !savedDate) return false;
    // messages should never overlap, we assume that it is configured logically

    today = startOfToday(today);
    // only render messages 1 through 3, return key and value, but make sure there is a value
    const messages = Object.values(MODALS).slice(0, 3).map(m => [m, config[m]]).filter(m => m[1]);
    const result = messages.find(([_, message]) => {
      if (Number.isInteger(message.time_offset_days)) {
        const startShowingMessageDate = addDays(savedDate, message.time_offset_days);
        if (Number.isInteger(message.expires_days)) {
          const stopShowingMessageDate = addDays(startShowingMessageDate, message.expires_days - 1);
          return isWithinInterval(today, { start: startShowingMessageDate, end: stopShowingMessageDate });
        } else {
          return today > startShowingMessageDate;
        }
      }
    });

    if (result) {
      const [activeKey, activeMessage] = result;
      const hasSeenMessage = seenTransitionMessage(activeKey, store.getters['user/get']);

      if (!hasSeenMessage) {
        // we need to include the key so we can record the modal as seen
        return {...activeMessage, key: activeKey };
      }
    }
  }
};

// previousClassification is the classification that the user transitioned away from
export function showPostTransitionMessage(store, previousClassification) {
  const config = getTransitionConfig(store, previousClassification);
  if (config) {
    const user = store.getters['user/get'];
    recordTransitionMessageSeen(MODALS.MESSAGE_4, { id: user.id, classificationId: previousClassification.id });
    return { ...config[MODALS.MESSAGE_4], key: MODALS.MESSAGE_4 };
  }
}

export function showTransitionBanner(store) {
  const hasSeenBannerableMessage = seenTransitionMessage(MODALS.MESSAGE_3, store.getters['user/get']);
  const hasSeenPostBannerMessage = seenTransitionMessage(MODALS.MESSAGE_4, store.getters['user/get']);
  if (hasSeenBannerableMessage && !hasSeenPostBannerMessage) {
    const config = getTransitionConfig(store, store.getters['session/classification']);
    const userHasPurchased = userHasExcludedPurchaseOrder(store, config);
    if (config && !userHasPurchased) {
      const message = config[MODALS.MESSAGE_3];
      if (message) {
        return message.banner;
      }
    }
  }
}

export function generateLocalStorageKey(user) {
  if (user) {
    return `mnm-transition-${user.classificationId}-modal-seen-${user.id}`;
  }
}
